import {
  isCardstackAssignment,
  isDesmosAssignmentPayload,
  Modify,
} from 'shared/lib/utils';
import { dateService } from 'shared/lib/date';
import { RELEVANT_DOMAINS } from 'shared/lib/domains';

import {
  Assignment,
  AssignmentContent,
  AssignmentContentPayload,
  AssignmentsPayload,
  AssignmentsResponse,
  Brand,
  EditAssignmentPayload,
} from './types';

type ContentWithUnknownDomain = Modify<AssignmentContent, { domain?: string }>;
export type AssignmentWithUnknownDomain = Modify<
  Assignment,
  { content: ContentWithUnknownDomain | ContentWithUnknownDomain[] }
>;

const getAssignmentDueDate = (assignment: AssignmentsPayload): Date | null => {
  const { dueDate } = assignment;

  if (dueDate) {
    return dateService.getUTCDateObject(dueDate);
  }

  return null;
};

const isCustomized = (
  content: AssignmentContentPayload,
): content is AssignmentContentPayload => {
  const maybeCustomized = content as AssignmentContent;

  return Boolean(maybeCustomized.customContentId && maybeCustomized.domain);
};

const isCustom = (
  content: AssignmentContentPayload,
): content is AssignmentContentPayload =>
  Boolean((content as AssignmentContentPayload).customContentId);

export const getBrand = (content: AssignmentContentPayload): Brand => {
  // Customized check should come before custom
  if (isCustomized(content)) {
    return Brand.CUSTOMIZED;
  }

  if (isCustom(content)) {
    return Brand.CUSTOM;
  }

  return Brand.REGULAR;
};

const normalizeAssignmentContent = (
  content: AssignmentContentPayload & { contentBusinessKey: string },
): ContentWithUnknownDomain => {
  return {
    ...content,
    brand: getBrand(content),
  };
};

const normalizeCardstackAssignment = (
  assignment: AssignmentsPayload,
): AssignmentWithUnknownDomain => {
  const content = assignment.assignedContent.reduce(
    (acc: ContentWithUnknownDomain[], item) => {
      acc.push(
        normalizeAssignmentContent({
          ...item.content,
          contentBusinessKey: assignment.assignedContent[0].contentBusinessKey,
        }),
      );
      return acc;
    },
    [],
  );

  return {
    ...assignment,
    type: 'Cardstack',
    startDate: assignment.startDate
      ? dateService.getValueOf(
          dateService.getUTCDateObject(assignment.startDate),
        )
      : null,
    dueDate: dateService.getValueOf(getAssignmentDueDate(assignment)),
    content,
  };
};

export const normalizeAssignments = (
  assignmentResponse: AssignmentsResponse | null,
): AssignmentWithUnknownDomain[] | null =>
  assignmentResponse &&
  assignmentResponse.map((assignment) => {
    if (isCardstackAssignment(assignment)) {
      return normalizeCardstackAssignment(assignment);
    }

    const assignmentType = isDesmosAssignmentPayload(assignment)
      ? 'desmos'
      : 'Lesson';

    return {
      ...assignment,
      type: assignmentType,
      startDate: assignment.startDate
        ? dateService.getValueOf(
            dateService.getUTCDateObject(assignment.startDate),
          )
        : null,
      dueDate: dateService.getValueOf(getAssignmentDueDate(assignment)),
      content: normalizeAssignmentContent({
        ...assignment.assignedContent[0].content,
        contentBusinessKey: assignment.assignedContent[0].contentBusinessKey,
      }),
    };
  });

// Let in all Custom assignments or assignments with relevant domain.
export const isRelevant = (
  assignment: AssignmentWithUnknownDomain,
): assignment is Assignment => {
  const assignmentContent = Array.isArray(assignment.content)
    ? assignment.content[0]
    : assignment.content;

  if (assignmentContent.brand === Brand.CUSTOM) return true;

  const assignmentDomain = assignmentContent.domain;

  if (!assignmentDomain) return false;

  return RELEVANT_DOMAINS.some((relevant_domain) =>
    assignmentDomain.startsWith(relevant_domain),
  );
};

export type AssignmentPayloadUpdatedFields = {
  dueDate?: number | null;
  startDate?: number | null;
  archived?: boolean;
};

export const convertAssignmentIntoAssignmentPayload = (
  assignment: Assignment,
  updatedFields?: AssignmentPayloadUpdatedFields,
): EditAssignmentPayload => {
  const { businessKey, type, content, classBks, dueDate, startDate, archived } =
    assignment;

  const currentDueDate =
    updatedFields?.dueDate === undefined ? dueDate : updatedFields.dueDate;
  const currentStartDate =
    updatedFields?.startDate === undefined
      ? startDate
      : updatedFields.startDate;
  const currentArchived = updatedFields?.archived ?? archived;

  const getUpdatedAssignedContent =
    (): AssignmentsPayload['assignedContent'] => {
      if (assignment.type === 'Cardstack') {
        const activeActivities = Array.isArray(content)
          ? content.filter((activity) => activity.isActivityActive !== false)
          : [];

        return activeActivities.map(
          ({ isActivityActive, ...activityContent }) => {
            return {
              type: 'Cardstack',
              content: activityContent,
              contentBusinessKey: activityContent.contentBusinessKey,
            };
          },
        );
      }

      return [
        {
          type,
          contentBusinessKey: assignment.content.contentBusinessKey,
          content: assignment.content,
        },
      ];
    };

  return {
    businessKey,
    assignedContent: getUpdatedAssignedContent(),
    classBks,
    dueDate: currentDueDate,
    startDate: currentStartDate,
    archived: currentArchived,
  };
};
